import type { RefOrGetter } from "ls/common/types";
import type { UseSearchOptions } from "./useLocalSearch";
import { computed, ref } from "vue";
import { deepToFolderHit, deepToShortcutHit, deepToWorkspaceHit, fuzzyFolderToHit, fuzzyShortcutToHit, fuzzyWorkspaceToHit } from "./adapters";
import { useSearchStore } from "./useCommandKControls";
import { useDeepSearch } from "./useDeepSearch";
import { useDeepSearchAllowed } from "./useDeepSearchAllowed";
import { useLocalSearch } from "./useLocalSearch";

export function useCombinedSearch(searchTerm: RefOrGetter<string>, options: UseSearchOptions = {}) {
  const store = useSearchStore();
  const { deepSearchAllowed } = useDeepSearchAllowed();

  const localResults = useLocalSearch(searchTerm, options);
  const useLocal = computed(() => store.nameOnly || !deepSearchAllowed.value);

  const deepOptions = {
    limit: ref(options.limit ?? 25),
    debounceMs: ref(300),
    enabled: computed(() => deepSearchAllowed.value && !store.nameOnly),
  };
  const deepResults = useDeepSearch(searchTerm, deepOptions);

  // deep search needs the contents of local's query (to map hits to shortcut/fs/etc. js)
  const isPending = computed(() => localResults.isPending.value || (!useLocal.value && deepResults.isPending.value));
  const isLoading = computed(() => localResults.isLoading.value || (!useLocal.value && deepResults.isLoading.value));
  const isFetching = computed(() => localResults.isFetching.value || (!useLocal.value && deepResults.isFetching.value));
  const isSuccess = computed(() => localResults.isSuccess.value || (!useLocal.value && deepResults.isSuccess.value));

  function notNull<T>(value: T | null): value is T {
    return value !== null;
  }

  const files = computed(() => useLocal.value ? localResults.files.value.map(f => fuzzyShortcutToHit(f)) : deepResults.projects.value.map(p => deepToShortcutHit(p, localResults.shortcutsMap.value)).filter(notNull));
  const workspaces = computed(() => useLocal.value ? localResults.workspaces.value.map(f => fuzzyWorkspaceToHit(f)) : deepResults.workspaces.value.map(p => deepToWorkspaceHit(p, localResults.workspacesMap.value)).filter(notNull));
  const folders = computed(() => useLocal.value ? localResults.folders.value.map(f => fuzzyFolderToHit(f)) : deepResults.folders.value.map(p => deepToFolderHit(p, localResults.foldersMap.value)).filter(notNull));

  const filesAndDeep = computed(() => {
    if (store.nameOnly) return files.value;
    const allHits = [
      ...files.value,
      ...deepResults.pages.value.map(h => deepToShortcutHit(h, localResults.shortcutsMap.value)).filter(notNull),
      ...deepResults.rpDeep.value.map(h => deepToShortcutHit(h, localResults.shortcutsMap.value)).filter(notNull),
      ...deepResults.canvasDeep.value.map(h => deepToShortcutHit(h, localResults.shortcutsMap.value)).filter(notNull),
      ...deepResults.docsDeep.value.map(h => deepToShortcutHit(h, localResults.shortcutsMap.value)).filter(notNull),
      ...deepResults.commentsDeep.value.map(h => deepToShortcutHit(h, localResults.shortcutsMap.value)).filter(notNull),
      ...deepResults.notesDeep.value.map(h => deepToShortcutHit(h, localResults.shortcutsMap.value)).filter(notNull),
    ];
    return allHits.sort((a, b) => (b.score ?? -Infinity) - (a.score ?? -Infinity));
  });

  return {
    filesAndDeep,
    workspaces,
    folders,
    isPending,
    isLoading,
    isFetching,
    isSuccess,
  };
}
